import { jwtDecode } from 'jwt-decode'
import { createTaggedLogger } from './logger'

const logger = createTaggedLogger('Auth')

const ACCESS_TOKEN_KEY = 'accessToken'
const REFRESH_TOKEN_KEY = 'refreshToken'

interface TokenPayload {
  exp: number
  iat: number
  userId: string
  email: string
  role: string
  merchantId?: number
}

export const setTokens = (accessToken: string, refreshToken: string): void => {
  logger.debug('Setting tokens')
  sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

export const getAccessToken = (): string | null => {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY)
}

export const getRefreshToken = (): string | null => {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY)
}

export const clearTokens = (): void => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY)
  sessionStorage.removeItem(REFRESH_TOKEN_KEY)
}

export const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<TokenPayload>(token)
    const currentTime = Date.now() / 1000
    return decoded.exp < currentTime
  } catch (error) {
    return true
  }
}

export const getTimeUntilExpiry = (token: string): number => {
  try {
    const decoded = jwtDecode<TokenPayload>(token)
    const currentTime = Date.now() / 1000
    return decoded.exp - currentTime
  } catch {
    return 0
  }
}

export const isAuthenticated = (): boolean => {
  const accessToken = getAccessToken()
  if (!accessToken) return false
  return !isTokenExpired(accessToken)
}

export const getUserFromToken = (): TokenPayload | null => {
  const accessToken = getAccessToken()
  if (!accessToken) return null

  try {
    return jwtDecode<TokenPayload>(accessToken)
  } catch {
    return null
  }
}

/**
 * 결제 상세의 환불·환불 링크·HOLDING 결정 버튼 노출 여부.
 * 서버 refundPayment·createRefundLink 는 ADMIN·MERCHANT_ADMIN 만 허용하고,
 * MERCHANT_ADMIN 은 자기 가맹점 결제에만 쓸 수 있다.
 * GraphQL Merchant.id 는 문자열, 토큰의 merchantId 는 숫자라 문자열로 맞춰 비교한다.
 */
export const canManagePaymentRefund = (
  user: { role: string; merchantId?: number | null } | null | undefined,
  paymentMerchantId: string | number | null | undefined
): boolean => {
  if (!user) return false
  if (user.role === 'ADMIN') return true
  return (
    user.role === 'MERCHANT_ADMIN' &&
    user.merchantId != null &&
    paymentMerchantId != null &&
    String(user.merchantId) === String(paymentMerchantId)
  )
}
